import { useEffect, useState } from 'react';
import {
  Controller,
  FieldErrors,
  FieldValues,
  RegisterOptions,
  UseControllerProps,
  useWatch,
} from 'react-hook-form';

import { ErrorMessage } from '@hookform/error-message';
import { CircleXIcon, RefreshCwIcon, SparklesIcon } from 'lucide-react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore posthog is in the parent dir
import { usePostHog } from 'posthog-js/react';

import {
  DebounceSelect,
  GenericLocationOption,
  ValueType,
} from 'components/DebounceSelect';
import { Label } from 'components/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/Select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/Tooltip';
import { hasRequiredValidation } from 'components/input/RHFTextInput';
import { Flex } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { Typography } from 'components/typography';
import { useFieldAttributes } from 'hooks/useLoadContext';
import {
  FieldAttributes,
  getFieldAttribute,
  initFieldAttributes,
} from 'types/LoadAttributes';
import { Maybe, Undef } from 'types/UtilityTypes';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import {
  CarrierSearchableFields,
  GenericCompanySearchableFields,
  normalizeExternalTMSID,
  resolveSelectedItem,
} from 'utils/loadInfoAndBuilding';
import { cn } from 'utils/shadcn';

type RHFDebounceSelectProps<TFieldValues extends FieldValues> = Pick<
  UseControllerProps<TFieldValues>,
  'name' | 'rules' | 'control' | 'disabled'
> & {
  label: string;
  errors: FieldErrors;
  data: Maybe<any[]>; // List of objects (locations/customers) which will then be mapped to options
  fetchOptions: (
    field: GenericCompanySearchableFields,
    searchTerm: string
  ) => Promise<ValueType[]>;
  mapOptions: (data: Maybe<any[]>) => ValueType[];
  refreshHandler?: () => void; // Optional — omit to hide refresh (e.g. LTL quick quote customer field)
  resetOptionsHandler?: () => void;
  isLoading: boolean;
  className?: string;
  labelClassName?: string;
  showRefreshButton?: boolean; // default true
  refreshDisabled?: boolean;
  refreshTooltip?: string;

  required?: boolean; // Default = true
  requiredIcon?: boolean;
  placeholder?: string; // Default = 'Search'
  showSearchParamDropdown?: boolean; // Default = true
  searchFieldDefault?: GenericCompanySearchableFields | CarrierSearchableFields; // Default = 'name'
  searchParamOptions?: {
    key: GenericCompanySearchableFields | CarrierSearchableFields;
    label: string;
  }[];
  // Optional fields for parent component to control the value/display of its child DebounceSelect input.
  // Example: The debounce select is used to search a location by street address, and when selected, we want to update the parent
  // `${stop}.externalTMSID` field with the selected location's externalTMSID,
  //  and `${stop}.addressLine1` with the selected location's addressLine1. See example usage in
  //   [McleodSectionForms/Stop.tsx](https://github.com/drumkitai/alexandria/pages/QuoteView/LoadBuilding/McleodSectionForms/Stop.tsx#L190-L210)
  controllingParentValue?: ValueType;
  valueRenderer?: (selectedOption: ValueType) => ValueType;
  parentOnChange?: (value: ValueType, selectedRawItem?: unknown) => void;
  // If true, when parentOnChange exists, call both parentOnChange and field.onChange
  // Otherwise, use original logic: parentOnChange ? parentOnChange(value) : field.onChange(valueToUse)
  callFieldOnChangeWithParent?: boolean;
  customActionButtons?: React.ReactNode; // Optional custom buttons to render next to refresh button
  useSimpleOptionRender?: boolean; // If true, renders options as simple labels instead of GenericLocationOption
  aiHighlightedExternalTMSID?: string; // If provided, the matching option will be highlighted with AI sparkle icon
};

// TODO: Use more type safety https://shorturl.at/SxZ7T
export function RHFDebounceSelect<TFieldValues extends FieldValues>({
  name,
  label,
  disabled,
  control,
  errors,
  rules,
  fetchOptions,
  mapOptions,
  data: initData,
  isLoading: initIsLoading,
  refreshHandler,
  resetOptionsHandler,
  controllingParentValue: initParentValue,
  valueRenderer,
  parentOnChange,
  callFieldOnChangeWithParent = false,
  useSimpleOptionRender = false,
  placeholder = 'Search',
  required = true,
  labelClassName,
  className,
  showSearchParamDropdown = true,
  searchFieldDefault = 'name',
  searchParamOptions = [
    { key: 'name', label: 'Name' },
    { key: 'addressLine1', label: 'Street' },
  ],
  showRefreshButton = true,
  refreshDisabled = false,
  refreshTooltip,
  customActionButtons,
  aiHighlightedExternalTMSID,
}: RHFDebounceSelectProps<TFieldValues>) {
  const allFieldAttrs = useFieldAttributes();
  const watchedFieldValue = useWatch({ control, name });

  const thisFieldAttr: FieldAttributes = (() => {
    // Handle fields of type `ValueUnit`
    const normalizedName = name.replace('.val', '');

    const res = getFieldAttribute(allFieldAttrs, normalizedName);

    return res ?? initFieldAttributes;
  })();

  required = required || hasRequiredValidation(rules as RegisterOptions);

  const [curParentValue, setCurParentValue] =
    useState<Undef<ValueType>>(initParentValue);
  const [curDataList, setCurDataList] = useState<Maybe<ValueType[]>>(initData);
  const [isLoadingDebounceSelect, setIsLoadingDebounceSelect] = useState(false);
  const [selectedObj, setSelectedObj] = useState<Maybe<ValueType>>(null);
  const [searchField, setSearchField] = useState<string>(searchFieldDefault);

  const posthog = usePostHog();

  useEffect(() => {
    if (initData) {
      setCurDataList(initData);
    }
  }, [initData]);

  useEffect(() => {
    setIsLoadingDebounceSelect(initIsLoading);
  }, [initIsLoading]);

  useEffect(() => {
    setCurParentValue(initParentValue);
  }, [initParentValue]);

  // Helper function to create user-friendly display value
  const getDisplayValue = (selectedOption: ValueType): ValueType => {
    if (valueRenderer) {
      return valueRenderer(selectedOption);
    }

    // For location selections, show name instead of ID
    const displayLabel = selectedOption.name || selectedOption.label;

    return {
      label: displayLabel,
      value: selectedOption.value,
    };
  };

  // Helper function to create user-friendly label
  const getFieldLabel = (): string => {
    // For location fields, just show the base label
    return label;
  };

  return (
    <Flex
      align='start'
      gap='sm'
      className={cn('w-full whitespace-nowrap', className)}
    >
      {/* Simplified search controls - only show dropdown when explicitly needed */}
      {showSearchParamDropdown &&
        !thisFieldAttr.isReadOnly &&
        searchParamOptions.length > 1 && (
          <div className='w-[85px] shrink-0'>
            <Label name='searchParam' className='font-medium'>
              Search by
            </Label>
            <Select
              value={searchField}
              disabled={disabled || thisFieldAttr.isReadOnly}
              onValueChange={(value) => {
                setSearchField(value);
                posthog?.capture(
                  ButtonNamePosthog.LoadBuildingLocationNameVsStreet,
                  {
                    searchField,
                  }
                );
              }}
            >
              <SelectTrigger className='h-8 w-[85px] text-xs mt-1!'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {searchParamOptions.map((option) => (
                  <SelectItem
                    key={option.key}
                    value={option.key}
                    className='text-xs'
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

      {/* Main search input section */}
      <div className='w-full overflow-auto'>
        <Label
          name={name}
          required={required}
          className={cn('text-[13px] font-medium', labelClassName)}
        >
          {getFieldLabel()}
        </Label>

        <Flex align='center' gap='sm'>
          <div className='flex-1 min-w-0'>
            <Controller
              name={name}
              control={control}
              disabled={disabled || thisFieldAttr.isReadOnly}
              rules={{ required: required ? 'Required' : undefined, ...rules }}
              render={({ field }) => {
                const currentValue = curParentValue ?? field.value;

                const currentValueStr =
                  currentValue != null ? String(currentValue) : '';

                let selectedOption: ValueType = mapOptions(curDataList).find(
                  (option) =>
                    option?.value != null &&
                    String(option.value) === currentValueStr
                );

                // Fallback to selectedObj if option not found in current data list
                // This ensures the display name persists even when the data list is
                // filtered/reset and no longer contains the selected item — but never use a stale
                // object whose externalTMSID does not match the form value (shows wrong name under ID).
                const normalizedFieldValue = normalizeExternalTMSID(
                  field.value
                );
                if (
                  !selectedOption &&
                  selectedObj &&
                  normalizedFieldValue &&
                  selectedObj.externalTMSID != null &&
                  normalizeExternalTMSID(selectedObj.externalTMSID) ===
                    normalizedFieldValue
                ) {
                  selectedOption = mapOptions([selectedObj])[0];
                }

                // Update selectedObj when field value matches an item in the data list
                const normalizedCurrentValue =
                  normalizeExternalTMSID(currentValueStr);
                if (normalizedCurrentValue && curDataList) {
                  const matchingObj = curDataList.find(
                    (item) =>
                      item?.externalTMSID != null &&
                      normalizeExternalTMSID(item.externalTMSID) ===
                        normalizedCurrentValue
                  );
                  if (
                    matchingObj &&
                    (!selectedObj ||
                      normalizeExternalTMSID(selectedObj.externalTMSID) !==
                        normalizedCurrentValue)
                  ) {
                    setSelectedObj(matchingObj);
                  }
                } else if (!normalizedCurrentValue && selectedObj) {
                  // Clear selectedObj when field is cleared
                  setSelectedObj(null);
                }

                return (
                  <Flex align='center' gap='sm' className='w-full'>
                    <div className='flex-1 min-w-0'>
                      <DebounceSelect
                        key={currentValue ? 'has-value' : 'no-value'}
                        showSearch
                        disabled={
                          isLoadingDebounceSelect ||
                          disabled ||
                          thisFieldAttr.isReadOnly
                        }
                        className='h-8 text-[13px] border-neutral-input-border mt-1!'
                        placeholder={
                          isLoadingDebounceSelect
                            ? 'Loading...'
                            : placeholder || 'Search locations...'
                        }
                        optionFilterProp='children'
                        fetchOptions={(search) =>
                          fetchOptions(
                            searchField as GenericCompanySearchableFields,
                            search
                          )
                        }
                        onChange={(value) => {
                          if (!value) {
                            field.onChange(null);
                            setSelectedObj(undefined);

                            return;
                          }

                          const valueToUse =
                            typeof value === 'object' && 'value' in value
                              ? value.value
                              : value;

                          const id = normalizeExternalTMSID(valueToUse);
                          const selectedObj = id
                            ? resolveSelectedItem<ValueType>(
                                id,
                                curDataList,
                                value,
                                value
                              )
                            : undefined;

                          // If callFieldOnChangeWithParent is true and parentOnChange exists, call both
                          // Otherwise, use original logic: parentOnChange ? parentOnChange(value) : field.onChange(valueToUse)
                          if (callFieldOnChangeWithParent && parentOnChange) {
                            field.onChange(valueToUse);
                            parentOnChange(value, selectedObj);
                          } else if (parentOnChange) {
                            parentOnChange(value, selectedObj);
                          } else {
                            field.onChange(valueToUse);
                          }

                          setSelectedObj(selectedObj);
                        }}
                        value={(() => {
                          if (selectedOption) {
                            return getDisplayValue(selectedOption);
                          }
                          if (field.value) {
                            return { label: 'Loading...', value: field.value };
                          }
                          // MUST be undefined, not null, otherwise the select is not controlled by React Hook Form
                          return undefined;
                        })()}
                        options={(() => {
                          return dedupeAndSortOptions(
                            mapOptions(curDataList),
                            aiHighlightedExternalTMSID
                          );
                        })()}
                        optionRender={(option) => {
                          let content: React.ReactNode;
                          if (useSimpleOptionRender) {
                            content = option?.label ? (
                              <span>{option.label}</span>
                            ) : null;
                          } else {
                            content = (
                              <GenericLocationOption
                                option={option}
                                optionFieldsToRender={[
                                  'addressLine1',
                                  'addressLine2',
                                  'city',
                                  'state',
                                  'zipCode',
                                ]}
                              />
                            );
                          }
                          if (!content) return null;

                          const isAISuggested =
                            aiHighlightedExternalTMSID != null &&
                            String(option?.value) ===
                              String(aiHighlightedExternalTMSID);

                          return (
                            <AiOptionHint active={isAISuggested}>
                              {content}
                            </AiOptionHint>
                          );
                        }}
                        notFoundContent={
                          <div className='p-4 text-center text-neutral-500'>
                            <div className='text-[13px]'>No results found</div>
                            <div className='text-xs mt-1'>
                              {name.includes('customer')
                                ? 'Try searching with 2 or more characters'
                                : 'Try searching with 3 or more characters'}
                            </div>
                          </div>
                        }
                        onOpenChange={(visible: boolean) =>
                          !visible && resetOptionsHandler
                            ? resetOptionsHandler()
                            : null
                        }
                        style={{
                          width: '100%',
                        }}
                      />
                    </div>

                    {/* Clear button - outside the select */}
                    {field.value && !required && (
                      <button
                        title='Clear selection'
                        onClick={() => {
                          field.onChange(null);
                          setSelectedObj(null);
                        }}
                        className='shrink-0 p-1 hover:text-error-600 rounded-full transition-colors'
                      >
                        <CircleXIcon className='w-4 h-4' />
                      </button>
                    )}
                  </Flex>
                );
              }}
            />
          </div>

          {/* Action buttons container - refresh and custom buttons grouped together */}
          {(refreshHandler && showRefreshButton) || customActionButtons ? (
            <Flex align='center' gap='xs'>
              {/* Refresh button - hidden for ThreeG TMS customer field (fetch customer flow in progress) */}
              {refreshHandler && showRefreshButton && (
                <TooltipProvider>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <button
                        title={refreshTooltip ? undefined : 'Refresh'}
                        onClick={refreshHandler}
                        type='button'
                        className={`shrink-0 py-2 px-1 hover:text-info-600 rounded-full transition-colors ${
                          isLoadingDebounceSelect || refreshDisabled
                            ? 'cursor-not-allowed opacity-50'
                            : 'cursor-pointer'
                        }`}
                        disabled={isLoadingDebounceSelect || refreshDisabled}
                      >
                        {isLoadingDebounceSelect ? (
                          <ButtonLoader className='mr-0' />
                        ) : (
                          <RefreshCwIcon className='h-4 w-4' />
                        )}
                      </button>
                    </TooltipTrigger>
                    {refreshTooltip && (
                      <TooltipContent className='text-[10px] w-[200px]'>
                        <p className='max-w-xs whitespace-normal break-words leading-snug'>
                          {refreshTooltip}
                        </p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              )}

              {/* Custom action buttons (e.g., create new location button) */}
              {customActionButtons}
            </Flex>
          ) : null}
        </Flex>

        {/* Show external TMS ID under the search input when selected */}
        {watchedFieldValue != null &&
          (typeof watchedFieldValue === 'string' ||
            typeof watchedFieldValue === 'number') &&
          (typeof watchedFieldValue !== 'string' ||
            watchedFieldValue.trim() !== '') && (
            <div className='text-xs text-neutral-500 mt-1'>
              ID: {String(watchedFieldValue)}
            </div>
          )}

        <ErrorMessage
          errors={errors}
          name={name}
          render={({ message }) => (
            <Typography variant='body-xs' className='text-error-500 mt-1'>
              {message}
            </Typography>
          )}
        />
      </div>
    </Flex>
  );
}

// Deduplicate, sort and maintain the AI suggested option at the top of the list
export function dedupeAndSortOptions(
  options: Undef<ValueType[]>,
  aiHighlightedExternalTMSID?: string
): Undef<ValueType[]> {
  if (!options?.length) return options;

  // Dedupe by value
  const seen = new Set<string>();

  const deduped = options.filter((o) => {
    const v = o?.value != null ? String(o.value) : '';
    if (!v || seen.has(v)) return false;
    seen.add(v);

    return true;
  });

  if (!aiHighlightedExternalTMSID) return deduped;

  const suggestedStr = String(aiHighlightedExternalTMSID);

  return [...deduped].sort((a, b) => {
    const aMatch = a?.value != null && String(a.value) === suggestedStr;
    const bMatch = b?.value != null && String(b.value) === suggestedStr;

    if (aMatch && !bMatch) return -1;
    if (!aMatch && bMatch) return 1;
    return 0;
  });
}

type AiOptionHintProps = React.PropsWithChildren<{
  active?: boolean;
}>;

const AiOptionHint = ({ active, children }: AiOptionHintProps) => {
  if (!active) return <>{children}</>;

  return (
    <div
      className='border-l-4 border-accent-500 pl-1 -ml-1 rounded flex items-center gap-1.5'
      title='AI suggested'
    >
      <SparklesIcon
        className='h-3 w-3 stroke-accent-600! shrink-0'
        strokeWidth={2}
        aria-label='AI suggested'
      />
      <span className='flex-1'>{children}</span>
    </div>
  );
};
